import { router } from "@inertiajs/react";
import { useState } from "react";

import ConfirmDialog from "@/components/ConfirmDialog";
import type { Column, PagyMeta, RowAction } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import FilterBar, { type FilterField } from "@/components/FilterBar";
import LinkButton from "@/components/LinkButton";
import { formatAmount, formatDate, formatTime } from "@/lib/formatting";
import { monthFilterValue, monthNumber, monthOptions } from "@/lib/months";
import { usePermissions } from "@/lib/permissions";
import { courses } from "@/routes";
import type { Course } from "@/types";

function exportUrl(path: string, filters: Record<string, string>): string {
  // Keep blank values: an explicit empty param (e.g. "status=") tells the
  // backend NOT to apply that filter's default.
  const qs = new URLSearchParams(Object.entries(filters)).toString();
  return qs ? `${path}?${qs}` : path;
}

type Props = {
  data: Course[];
  pagy: PagyMeta;
  filters: Record<string, string>;
  categories: string[];
  houses: string[];
  centers: { id: number; name: string }[];
  amounts: number[];
};

const USER_CATEGORY_LABELS: Record<string, string> = {
  senora: "Señora",
  universitaria: "Universitaria",
  numeraria_auxiliar: "Numeraria Auxiliar",
  agregada: "Agregada",
  numeraria: "Numeraria",
  la_cuesta: "S V",
  la_cuesta_sn: "S SV",
  la_cuesta_cp: "CP SV",
};

const USER_CATEGORY_OPTIONS = [
  { label: "Todas", value: "" },
  ...Object.entries(USER_CATEGORY_LABELS).map(([value, label]) => ({
    label,
    value,
  })),
];

const ORIGIN_LABELS: Record<string, string> = {
  los_tilos: "Mujer",
  la_cuesta: "Hombre",
};

// Only a house_manager's Cursos view spans both foundations (their house
// hosts courses from both) — admin/director stay confined to their own
// origin, so this column would be redundant (always the same value) there.
const ORIGIN_COLUMN: Column<Course> = {
  header: "Sexo",
  cell: (c) => ORIGIN_LABELS[c.origin] ?? "-",
};

const COLUMNS: Column<Course>[] = [
  {
    header: "Identificador",
    cell: (c) => (c.identifier as string | null) || "-",
  },
  {
    header: "Tipo de curso",
    cell: (c) => (c.category_name as string | null) || "-",
  },
  {
    header: "Fecha Inicio",
    cell: (c) => formatDate(c.start_date),
  },
  {
    header: "Hora",
    cell: (c) => formatTime(c.start_time),
  },
  {
    header: "Fecha Término",
    cell: (c) => formatDate(c.end_date),
  },
  {
    header: "Hora",
    cell: (c) => formatTime(c.end_time),
  },
  {
    header: "Casa",
    cell: (c) => (c.house_name as string | null) || "-",
  },
  {
    header: "Aporte",
    cell: (c) => formatAmount(c.amount),
  },
  {
    header: "Inscripciones",
    cell: (c) => (c.enrollments_summary as string | null) || "-",
  },
  {
    header: "Categoría",
    cell: (c) =>
      c.user_category
        ? (USER_CATEGORY_LABELS[c.user_category] ?? c.user_category)
        : "-",
  },
  {
    header: "Público/Privado",
    cell: (c) => (c.is_public ? "Público" : "Privado"),
  },
  {
    header: "Centro a Cargo",
    cell: (c) => (c.center_name as string | null) || "-",
  },
];

export default function CoursesIndex({
  data: rows,
  pagy,
  filters,
  categories,
  houses,
  centers,
  amounts,
}: Props) {
  const permissions = usePermissions();

  const [deleteTarget, setDeleteTarget] = useState<Course | null>(null);

  const actions: RowAction<Course>[] = [
    {
      label: permissions.canEditCourse ? "Ver y editar" : "Ver",
      href: (c) => courses.show(c.id).url,
      variant: "primary",
    },
    ...(permissions.canManageAdminOnlyRecords
      ? ([
          {
            label: "Eliminar",
            onClick: (c) => setDeleteTarget(c),
            variant: "danger",
          },
        ] satisfies RowAction<Course>[])
      : []),
  ];

  const filterFields: FilterField[] = [
    {
      key: "identifier",
      label: "Identificador",
      type: "text",
      placeholder: "Buscar por identificador...",
    },
    {
      key: "category",
      label: "Tipo de Curso",
      type: "select",
      options: [
        { label: "Todos", value: "" },
        ...categories.map((name) => ({ label: name, value: name })),
      ],
    },
    {
      key: "house",
      label: "Casa",
      type: "select",
      options: [
        { label: "Todas", value: "" },
        ...houses.map((name) => ({ label: name, value: name })),
      ],
    },
    {
      key: "month",
      label: "Mes de realización",
      type: "select",
      options: monthOptions(),
    },
    ...(permissions.isAdmin
      ? ([
          {
            key: "amount",
            label: "Aporte",
            type: "select",
            options: [
              { label: "Todos", value: "" },
              ...amounts.map((amount) => ({
                label: formatAmount(amount),
                value: String(amount),
              })),
            ],
          },
        ] satisfies FilterField[])
      : []),
    ...(permissions.canViewAdminCourseFilters
      ? ([
          {
            key: "user_category",
            label: "Categorías de usuario",
            type: "select",
            options: USER_CATEGORY_OPTIONS,
          },
          {
            key: "course_center",
            label: "Centro a Cargo",
            type: "select",
            options: [
              { label: "Todos", value: "" },
              ...centers.map((c) => ({
                label: c.name,
                value: String(c.id),
              })),
            ],
          },
          {
            key: "is_public",
            label: "Público/Privado",
            type: "select",
            options: [
              { label: "Todos", value: "" },
              { label: "Público", value: "true" },
              { label: "Privado", value: "false" },
            ],
          },
        ] satisfies FilterField[])
      : []),
    ...(permissions.isHouseManager
      ? ([
          {
            key: "origin",
            label: "Sexo",
            type: "select",
            options: [
              { label: "Todos", value: "" },
              { label: "Mujer", value: "los_tilos" },
              { label: "Hombre", value: "la_cuesta" },
            ],
          },
        ] satisfies FilterField[])
      : []),
  ];

  const columns = permissions.isHouseManager
    ? COLUMNS.flatMap((column) =>
        column.header === "Categoría" ? [column, ORIGIN_COLUMN] : [column],
      )
    : COLUMNS;

  function navigate(
    params: Record<string, string | number>,
    opts?: { replace?: boolean },
  ) {
    router.get(courses.index().url, params, {
      preserveScroll: true,
      replace: opts?.replace ?? false,
    });
  }

  return (
    <>
      <div className="w-full py-10">
        <div className="px-6 sm:px-8 lg:px-12">
          <div className="mb-6 flex items-center justify-between gap-4">
            <h1 className="font-sans text-3xl font-semibold text-ink">
              Cursos
            </h1>

            {permissions.canManageAdminOnlyRecords && (
              <LinkButton
                href={courses.new().url}
                variant="primary"
                className="px-4 py-1.5 text-sm"
              >
                Nuevo
              </LinkButton>
            )}
          </div>

          <FilterBar
            fields={filterFields}
            values={{ ...filters, month: monthNumber(filters.month) }}
            onChange={(newFilters) =>
              navigate(
                {
                  ...newFilters,
                  month: monthFilterValue(newFilters.month),
                  page: 1,
                },
                { replace: true },
              )
            }
          />
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          keyExtractor={(c) => c.id}
          actions={actions}
          utilities={
            permissions.canExportCourses ? (
              <a
                href={exportUrl("/courses/export_excel", filters)}
                className="font-sans text-sm font-light text-ink underline underline-offset-2 hover:text-steel"
              >
                Exportar Excel
              </a>
            ) : undefined
          }
          pagy={pagy}
          onPageChange={(page) => navigate({ ...filters, page })}
          emptyMessage="No hay cursos registrados."
        />
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Eliminar curso"
        message={`¿Estás seguro? Se eliminará el curso ${deleteTarget?.name ?? ""} y sus inscripciones.`}
        confirmLabel="Eliminar"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) {
            router.delete(courses.destroy(deleteTarget.id).url, {
              preserveScroll: true,
            });
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
